// src/context/StoreContext.jsx — v14
// CHANGED FROM v13 (5 Aug 2026): REAL FIX — updated to work with
// firebase.js v2's deferred Auth loading (see that file's changelog
// for the full real-evidence root cause). Every function that
// previously referenced the module-level `auth` object or imported
// firebase/auth functions directly now calls the new async
// getFirebaseAuth() / getGoogleProvider() from firebase.js instead —
// this is what actually delays the Auth SDK (and its iframe load)
// until AFTER the page has painted, instead of before. The main
// onAuthStateChanged listener now sets up inside an async IIFE within
// its useEffect. syncCart/syncWishlist/syncAddresses/setRole/
// saveWhatsappNumber/loginWithGoogle/loginWithPhoneToken/logout are
// all now async internally — none of their EXTERNAL behavior changed
// (same return values, same fire-and-forget pattern for the sync
// functions, same awaited-promise pattern for the rest) — callers
// elsewhere in the app (Login.jsx, ProductCard.jsx, ProductDetail.jsx,
// Cart.jsx, Account.jsx, etc.) do NOT need any changes, since useStore()
// still exposes the exact same function signatures as before.
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getFirebaseAuth, getGoogleProvider } from '../utils/firebase';
const StoreContext = createContext();
export function StoreProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [defaultAddressId, setDefaultAddressIdState] = useState('');
  const cartRef = useRef([]);
  const wishlistRef = useRef([]);
  useEffect(() => { cartRef.current = cart; }, [cart]);
  useEffect(() => { wishlistRef.current = wishlist; }, [wishlist]);
  useEffect(() => {
    let cancelled = false;
    let unsubscribe = () => {};
    (async () => {
      const { auth, authModule } = await getFirebaseAuth();
      if (cancelled) return;
      unsubscribe = authModule.onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          setProfileLoading(true);
          const baseUser = {
            uid: firebaseUser.uid,
            name: firebaseUser.displayName || 'Customer',
            email: firebaseUser.email || '',
            photo: firebaseUser.photoURL || '',
            phone: '',
            customerId: '',
            role: '',
            type: 'customer',
            wallet: null,
          };
          setUser(baseUser);
          setSavedAddresses([]);
          setDefaultAddressIdState('');
          try {
            const res = await fetch('/.netlify/functions/get-profile', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ uid: firebaseUser.uid }),
            });
            const data = await res.json();
            if (res.ok && data.exists) {
              setUser(prev => (prev && prev.uid === firebaseUser.uid) ? {
                ...prev,
                phone: data.phone || '',
                customerId: data.customerId || '',
                role: data.role || '',
                name: data.name || prev.name,
                email: data.email || prev.email,
                wallet: data.walletCache || null,
              } : prev);
              setSavedAddresses(data.addresses || []);
              setDefaultAddressIdState(data.defaultAddressId || '');
              const remoteCart = data.cart || [];
              if (remoteCart.length > 0) {
                setCart(remoteCart);
              } else if (cartRef.current.length > 0) {
                setCart(cartRef.current);
                firebaseUser.getIdToken().then(idToken => {
                  fetch('/.netlify/functions/save-cart', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ uid: firebaseUser.uid, cart: cartRef.current, idToken }),
                  }).catch(err => console.error('save-cart (initial push) failed:', err));
                });
              }
              const remoteWishlist = data.wishlist || [];
              if (remoteWishlist.length > 0) {
                setWishlist(remoteWishlist);
              } else if (wishlistRef.current.length > 0) {
                setWishlist(wishlistRef.current);
                firebaseUser.getIdToken().then(idToken => {
                  fetch('/.netlify/functions/save-wishlist', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ uid: firebaseUser.uid, wishlist: wishlistRef.current, idToken }),
                  }).catch(err => console.error('save-wishlist (initial push) failed:', err));
                });
              }
            }
          } catch (err) {
            console.error('get-profile fetch failed:', err);
          }
          setProfileLoading(false);
        } else {
          setUser(null);
          setSavedAddresses([]);
          setDefaultAddressIdState('');
          setCart([]);
          setWishlist([]);
          setProfileLoading(false);
        }
        setAuthLoading(false);
      });
    })();
    return () => { cancelled = true; unsubscribe(); };
  }, []);
  async function syncCart(nextCart) {
    setCart(nextCart);
    if (!user) return;
    try {
      const { auth } = await getFirebaseAuth();
      if (!auth.currentUser) return;
      const idToken = await auth.currentUser.getIdToken();
      await fetch('/.netlify/functions/save-cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid, cart: nextCart, idToken }),
      });
    } catch (err) {
      console.error('save-cart failed:', err);
    }
  }
  function addToCart(product, qty = 1, selection = {}) {
    const size = selection.size || null;
    const colorCode = selection.colorCode || null;
    const colorImageUrl = selection.colorImageUrl || null;
    const needsSize = !!product.hasSizing && (product.category === 'Garments' || product.category === 'Shoes');
    const needsColor = (product.images?.length || 0) > 1;
    if (needsSize && !size) {
      return { error: 'Please select a size before adding this item to your cart.' };
    }
    if (needsColor && !colorCode) {
      return { error: 'Please select a color before adding this item to your cart.' };
    }
    const key = `${product.id}__${size || 'NA'}__${colorCode || 'NA'}`;
    const existing = cart.find(i => i.key === key);
    let next;
    if (existing) {
      next = cart.map(i => i.key === key ? { ...i, qty: i.qty + qty } : i);
    } else {
      next = [...cart, { ...product, qty, size, colorCode, colorImageUrl, key }];
    }
    syncCart(next);
    return { ok: true, key };
  }
  function updateCartItemSelection(oldKey, product, selection, qty) {
    const size = selection.size || null;
    const colorCode = selection.colorCode || null;
    const colorImageUrl = selection.colorImageUrl || null;
    const needsSize = !!product.hasSizing && (product.category === 'Garments' || product.category === 'Shoes');
    const needsColor = (product.images?.length || 0) > 1;
    if (needsSize && !size) {
      return { error: 'Please select a size before continuing.' };
    }
    if (needsColor && !colorCode) {
      return { error: 'Please select a color before continuing.' };
    }
    const newKey = `${product.id}__${size || 'NA'}__${colorCode || 'NA'}`;
    const withoutOld = cart.filter(i => i.key !== oldKey);
    const existingAtNewKey = withoutOld.find(i => i.key === newKey);
    let next;
    if (existingAtNewKey) {
      next = withoutOld.map(i => i.key === newKey ? { ...i, qty: i.qty + qty } : i);
    } else {
      next = [...withoutOld, { ...product, qty, size, colorCode, colorImageUrl, key: newKey }];
    }
    syncCart(next);
    return { ok: true, key: newKey };
  }
  function removeFromCart(key) {
    syncCart(cart.filter(i => i.key !== key));
  }
  function updateQty(key, qty) {
    if (qty < 1) return removeFromCart(key);
    syncCart(cart.map(i => i.key === key ? { ...i, qty } : i));
  }
  function clearCart() { syncCart([]); }
  async function syncWishlist(nextWishlist) {
    setWishlist(nextWishlist);
    if (!user) return;
    try {
      const { auth } = await getFirebaseAuth();
      if (!auth.currentUser) return;
      const idToken = await auth.currentUser.getIdToken();
      await fetch('/.netlify/functions/save-wishlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid, wishlist: nextWishlist, idToken }),
      });
    } catch (err) {
      console.error('save-wishlist failed:', err);
    }
  }
  function toggleWishlist(productId) {
    const next = wishlist.includes(productId)
      ? wishlist.filter(id => id !== productId)
      : [...wishlist, productId];
    syncWishlist(next);
  }
  async function syncAddresses(nextAddresses, nextDefaultId) {
    setSavedAddresses(nextAddresses);
    setDefaultAddressIdState(nextDefaultId);
    if (!user) return;
    try {
      const { auth } = await getFirebaseAuth();
      if (!auth.currentUser) return;
      const idToken = await auth.currentUser.getIdToken();
      await fetch('/.netlify/functions/save-addresses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid, addresses: nextAddresses, defaultAddressId: nextDefaultId, idToken }),
      });
    } catch (err) {
      console.error('save-addresses failed:', err);
    }
  }
  function saveAddress(address) {
    const exists = savedAddresses.find(a => a.id === address.id);
    let next;
    if (exists) {
      next = savedAddresses.map(a => a.id === address.id ? address : a);
    } else {
      next = [...savedAddresses, { ...address, id: Date.now().toString() }];
    }
    const nextDefault = defaultAddressId || (next.length === 1 ? next[0].id : defaultAddressId);
    syncAddresses(next, nextDefault);
  }
  function deleteAddress(id) {
    const next = savedAddresses.filter(a => a.id !== id);
    let nextDefault = defaultAddressId;
    if (defaultAddressId === id) {
      nextDefault = next.length > 0 ? next[0].id : '';
    }
    syncAddresses(next, nextDefault);
  }
  function setDefaultAddress(id) {
    syncAddresses(savedAddresses, id);
  }
  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.qty, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.qty, 0);
  async function loginWithGoogle() {
    const { auth, authModule } = await getFirebaseAuth();
    const googleProvider = await getGoogleProvider();
    googleProvider.setCustomParameters({ prompt: 'select_account' });
    const result = await authModule.signInWithPopup(auth, googleProvider);
    const fbUser = result.user;
    let profileInfo = { isNew: false, customerId: '', phone: '', role: '' };
    try {
      const res = await fetch('/.netlify/functions/ensure-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: fbUser.uid, email: fbUser.email, name: fbUser.displayName }),
      });
      const data = await res.json();
      if (res.ok) profileInfo = data;
    } catch (err) {
      console.error('ensure-profile fetch failed:', err);
    }
    setUser(prev => ({
      ...(prev || {}),
      uid: fbUser.uid,
      name: fbUser.displayName || 'Customer',
      email: fbUser.email || '',
      photo: fbUser.photoURL || '',
      phone: profileInfo.phone || '',
      customerId: profileInfo.customerId || '',
      role: profileInfo.role || '',
      type: 'customer',
    }));
    return { user: fbUser, ...profileInfo };
  }
  async function setRole(role) {
    const { auth } = await getFirebaseAuth();
    if (!user || !auth.currentUser) return { error: 'Not signed in' };
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch('/.netlify/functions/set-role', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid, role, idToken }),
      });
      const data = await res.json();
      if (!res.ok) return { error: data.error || 'Could not save role' };
      setUser(prev => ({ ...prev, role: data.role }));
      return { ok: true };
    } catch (err) {
      return { error: 'Network error saving role' };
    }
  }
  async function saveWhatsappNumber(number) {
    const { auth } = await getFirebaseAuth();
    if (!user || !auth.currentUser) return { error: 'Not signed in' };
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch('/.netlify/functions/save-phone', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid, phone: number, email: user.email, name: user.name, idToken }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { error: data.error || 'Could not save number' };
      }
      setUser(prev => ({ ...prev, phone: data.phone }));
      return { ok: true };
    } catch (err) {
      return { error: 'Network error saving number' };
    }
  }
  async function loginWithPhoneToken(accessToken) {
    try {
      const res = await fetch('/.netlify/functions/verify-otp-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken }),
      });
      const data = await res.json();
      if (res.status === 404) {
        return { status: 'not_found' };
      }
      if (!res.ok) {
        return { error: data.error || 'Verification failed' };
      }
      const { auth, authModule } = await getFirebaseAuth();
      await authModule.signInWithCustomToken(auth, data.token);
      setUser(prev => ({
        ...prev,
        phone: data.phone,
        customerId: data.customerId || prev?.customerId || '',
        role: data.role || prev?.role || '',
      }));
      return { status: 'ok' };
    } catch (err) {
      return { error: 'Network error verifying OTP' };
    }
  }
  async function logout() {
    const { auth, authModule } = await getFirebaseAuth();
    await authModule.signOut(auth);
  }
  return (
    <StoreContext.Provider value={{
      cart, addToCart, updateCartItemSelection, removeFromCart, updateQty, clearCart,
      cartTotal, cartCount,
      user, authLoading, profileLoading, loginWithGoogle, logout, saveWhatsappNumber, loginWithPhoneToken, setRole,
      wishlist, toggleWishlist,
      savedAddresses, saveAddress, deleteAddress, defaultAddressId, setDefaultAddress,
    }}>
      {children}
    </StoreContext.Provider>
  );
}
export function useStore() {
  return useContext(StoreContext);
}